import { observable, action, runInAction, makeObservable, computed } from 'mobx';
import { LocalStorage } from '@vromo/models';
import { ObservableSet } from '@vromo/mobx-react';
import { Coords } from '@vromo/models';
import { persist } from 'mobx-persist';

type Filters = {
    jobs: {
        vehicles: number[];
        search: string;
        status: 'active' | 'all' | 'open' | 'driver' | 'underway' | 'completed' | 'cancelled';
        startDate: number | null;
        endDate: number | null;
        order: 'risk' | 'deadline' | 'name' | 'created' | 'group';
        type: 'delivery' | 'all' | 'route';
        teams: number[];
        sites: number[];
        policies: number[];

        clear: () => void;

        count: number;
    };
    drivers: {
        offline: boolean;
        teams: number[];
        search: string;

        clear: () => void;

        count: number;
    };
};

/** Keep track of global UI state e,g, selected drivers and jobs, sorting, filtering */
class ViewStore {
    location = new Coords(-32.004427, 115.819437);
    callingCode = '1';
    country = 'US';
    currencyCode = 'USD';
    imperialUnits: boolean | null = null;
    /** allow debug info to be rendered. 
     // todo: to be enabled if '?debug' string is found in url
     */
    debug = false;
    openChat: number | null = null;
    activeChats = observable.array<number>();
    chatOpenedTimestamps = observable.map<string, number>();
    visibleGroups: number[] = [];
    visibleVehicles: number[] = [];
    visibleJobs: number[] = [];
    detailedViewZoomLevel = false;
    showingBetaBanner = false;

    darkMode = LocalStorage.darkMode !== undefined ? LocalStorage.darkMode : true;

    /** collection of selected records (generic) */
    selected = new ObservableSet<number>();
    /** collection of selected jobs */
    selectedJobs = new ObservableSet<number>();
    /** collection of selected vehicles */
    selectedVehicles = new ObservableSet<number>();
    /** advanced details view - full width view. Used for listing jobs */
    dataView = false;

    get isDetailedView() {
        return LocalStorage.enterpriseDispatch ? this.detailedViewZoomLevel : true;
    }

    addToActiveChats = (newChats: number[]) => {
        this.activeChats.push(...newChats);
    };

    setChatToRead = (id: number, when: number) => {
        this.chatOpenedTimestamps.set(`${id}`, when);
    };

    select = (ids: number[], modifier = false) => {
        if (modifier) {
            ids.forEach((id) => {
                if (this.selected.has(id)) {
                    this.selected.delete(id);
                } else {
                    this.selected.add(id);
                }
            });
        } else {
            this.selected.set(ids);
        }
    };

    tab = 'jobs';

    filters: Filters = {
        jobs: {
            vehicles: [],
            search: '',
            status: 'active',
            type: 'all',
            startDate: null,
            endDate: null,
            order: 'risk',

            teams: [],
            sites: [],
            policies: [],

            clear() {
                runInAction(() => {
                    this.vehicles = [];
                    this.search = '';
                    this.status = 'active';
                    this.type = 'all';
                    this.order = 'risk';
                    this.teams = [];
                    this.sites = [];
                    this.policies = [];
                    this.startDate = null;
                    this.endDate = null;
                });
            },

            get count() {
                const defaultDiff = [
                    this.vehicles.length !== 0,
                    this.status !== 'active',
                    this.type !== 'all',
                    this.order !== 'risk',
                    this.teams.length !== 0,
                    this.sites.length !== 0,
                    this.policies.length !== 0,
                    this.startDate !== null && this.endDate !== null
                ];

                return defaultDiff.reduce((total, it) => (it ? total + 1 : total), 0);
            }
        },
        drivers: {
            offline: false, // Dont show offline drivers by default
            teams: [],
            search: '',

            clear() {
                runInAction(() => {
                    this.offline = false;
                    this.teams = [];
                    this.search = '';
                });
            },

            get count() {
                const defaultDiff = [this.offline !== false, this.teams.length !== 0];

                return defaultDiff.reduce((total, it) => (it ? total + 1 : total), 0);
            }
        }
    };

    constructor() {
        makeObservable(this, {
            openChat: observable,
            activeChats: observable,
            imperialUnits: observable,
            debug: observable,
            location: observable,
            callingCode: observable,
            country: observable,
            currencyCode: observable,
            darkMode: observable,
            selected: observable,
            selectedJobs: observable,
            selectedVehicles: observable,
            showingBetaBanner: observable,
            dataView: observable,
            visibleGroups: observable,
            visibleVehicles: observable,
            detailedViewZoomLevel: observable,
            visibleJobs: observable,
            select: action,
            tab: observable,
            filters: observable,
            isDetailedView: computed,
            addToActiveChats: action,
            setChatToRead: action
        });
    }
}

export default persist({
    openChat: true,
    activeChats: {
        type: 'list'
    },
    imperialUnits: true,
    chatOpenedTimestamps: {
        type: 'map'
    }
})(ViewStore);
